import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Redirect } from "react-router-dom";
import {
  FaGraduationCap,
  FaKey,
  FaCopy,
  FaShareAlt,
  FaSyncAlt,
  FaUser,
  FaSchool,
  FaWhatsapp,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import "../css/Admin_css/my-profile.css";
import ChangePassword from "./ChangePassword";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const REFERRAL_URL_BASE = (process.env.REACT_APP_REFERRAL_URL || "").trim();

function buildReferralUrl(referalCode) {
  const code = String(referalCode || "").trim();
  if (!REFERRAL_URL_BASE || !code) return "";

  const base = REFERRAL_URL_BASE.endsWith("/")
    ? REFERRAL_URL_BASE
    : `${REFERRAL_URL_BASE}/`;

  return `${base}${code}`;
}

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

function displayValue(value) {
  if (value === null || value === undefined || String(value).trim() === "") {
    return "—";
  }
  return String(value);
}

function formatCurrency(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDateOnly(value) {
  if (!value) return "—";
  return String(value).slice(0, 10);
}

function buildSchoolAddress(school) {
  const parts = [school?.school_address, school?.state, school?.country].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : "";
}

function getSchoolContact(school) {
  return {
    name: school?.admin_name || "",
    phone: school?.admin_phone || "",
  };
}

const BILLING_STATUS_TONE = {
  billed: "billed",
  pending: "pending",
  expired: "expired",
  "not billed": "not-billed",
};

function BillingStatusBadge({ status }) {
  const label = displayValue(status);
  const tone = BILLING_STATUS_TONE[String(status || "").toLowerCase()] || "not-billed";
  return <span className={`mp-referral-billing-badge mp-referral-billing-badge--${tone}`}>{label}</span>;
}

function getInitials(name) {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return parts[0].slice(0, 2).toUpperCase();
}

function CompactField({ label, value, fullWidth = false, tone = "work" }) {
  const empty = displayValue(value) === "—";
  return (
    <div className={`mp-field mp-field--${tone}${fullWidth ? " mp-field--full" : ""}`}>
      <span className={`mp-field__label mp-field__label--${tone}`}>{label}</span>
      <span className={`mp-field__value${empty ? " mp-field__value--empty" : ""}`}>
        {displayValue(value)}
      </span>
    </div>
  );
}

function ReferralsSection({ referalCode, referredSchools }) {
  const referralUrl = buildReferralUrl(referalCode);

  const handleCopyCode = async () => {
    if (!referalCode) {
      myToast.notify_warning("Referral code is not available yet.");
      return;
    }

    try {
      await navigator.clipboard.writeText(referalCode);
      myToast.notify_success("Referral code copied.");
    } catch (error) {
      console.error("[MyProfile] copy code error", error);
      myToast.notify_warning("Could not copy referral code.");
    }
  };

  const handleCopyLink = async () => {
    if (!referralUrl) {
      myToast.notify_warning("Referral link is not available yet.");
      return;
    }

    try {
      await navigator.clipboard.writeText(referralUrl);
      myToast.notify_success("Referral link copied.");
    } catch (error) {
      console.error("[MyProfile] copy link error", error);
      myToast.notify_warning("Could not copy referral link.");
    }
  };

  const handleRefer = () => {
    if (!referralUrl) {
      myToast.notify_warning("Referral link is not available yet.");
      return;
    }

    const shareText = `Join EduOK using my referral link: ${referralUrl}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <ProfileCard title="Referrals Management" icon={FaShareAlt} tone="referral">
      <div className="mp-referral-panel">
        <div className="mp-referral-code-box">
          <div className="mp-referral-code-box__top">
            <span className="mp-referral-code-box__label">Your referral code</span>
            <div className="mp-referral-code-box__actions">
              <button 
                type="button"
                className="mp-referral-refer-btn"
                onClick={handleRefer}
              >
                <FaWhatsapp aria-hidden /> Refer School
              </button>
            </div>
          </div>
          <div className="mp-referral-code-box__code-row">
            <span
              className={`mp-referral-code-box__code${
                !referalCode ? " mp-referral-code-box__code--empty" : ""
              }`}
            >
              {displayValue(referalCode)}
            </span>
            <button
              type="button"
              className="mp-referral-copy-icon-btn"
              onClick={handleCopyCode}
              disabled={!referalCode}
              aria-label="Copy referral code"
            >
              <FaCopy aria-hidden />
            </button>
          </div>
          <div className="mp-referral-code-box__url-row">
            <span className="mp-referral-code-box__url-label">Referral link</span>
            <span
              className={`mp-referral-code-box__url${
                !referralUrl ? " mp-referral-code-box__url--empty" : ""
              }`}
            >
              {referralUrl || "—"}
            </span>
            <button
              type="button"
              className="mp-referral-copy-icon-btn"
              onClick={handleCopyLink}
              disabled={!referralUrl}
              aria-label="Copy referral link"
            >
              <FaCopy aria-hidden />
            </button>
          </div>
          <span className="mp-referral-code-box__hint">
            Share your referral link on WhatsApp when referring a new school to EduOK.
          </span>
        </div>

        <div className="mp-referral-schools">
          <div className="mp-referral-schools__head">
            <span className="mp-referral-schools__title">Referred schools</span>
            <span className="mp-referral-schools__count">
              {referredSchools.length} school{referredSchools.length === 1 ? "" : "s"}
            </span>
          </div>

          {referredSchools.length === 0 ? (
            <div className="mp-referral-empty">
              <FaSchool aria-hidden />
              <span>No referred schools yet</span>
            </div>
          ) : (
            <div className="mp-referral-list">
              {referredSchools.map((school, index) => {
                const schoolKey =
                  school.school_code ||
                  school.referred_school_code ||
                  school.school_name ||
                  `school-${index}`;
                const schoolName =
                  school.school_name || school.referred_school_name || school.school_code;
                const schoolCode = school.school_code || school.referred_school_code || "";
                const billing = school.billing || {};
                const contact = getSchoolContact(school);
                const address = buildSchoolAddress(school);

                return (
                  <div className="mp-referral-card mp-referral-card--detailed" key={schoolKey}>
                    <div className="mp-referral-card__header">
                      <span className="mp-referral-card__icon" aria-hidden>
                        <FaSchool />
                      </span>
                      <div className="mp-referral-card__body">
                        <div className="mp-referral-card__name">{displayValue(schoolName)}</div>
                        {schoolCode ? (
                          <div className="mp-referral-card__meta">Code: {schoolCode}</div>
                        ) : null}
                      </div>
                      <div className="mp-referral-card__aside">
                        {school.date_added ? (
                          <span className="mp-referral-card__date">
                            {formatDateOnly(school.date_added)}
                          </span>
                        ) : null}
                      </div>
                    </div>

                    <div className="mp-referral-card__details">
                      {contact.name || contact.phone ? (
                        <div className="mp-referral-card__detail-row">
                          <span className="mp-referral-card__detail-label">Contact</span>
                          <span className="mp-referral-card__detail-value">
                            {[contact.name, contact.phone].filter(Boolean).join(" · ") || "—"}
                          </span>
                        </div>
                      ) : null}
                      {address ? (
                        <div className="mp-referral-card__detail-row">
                          <span className="mp-referral-card__detail-label">Address</span>
                          <span className="mp-referral-card__detail-value">{address}</span>
                        </div>
                      ) : null}
                      <div className="mp-referral-card__billing-grid">
                        <div className="mp-referral-card__billing-item">
                          <span className="mp-referral-card__billing-label">Billing status</span>
                          <span className="mp-referral-card__billing-value">
                            <BillingStatusBadge status={billing.billing_status} />
                          </span>
                        </div>
                        <div className="mp-referral-card__billing-item">
                          <span className="mp-referral-card__billing-label">Balance</span>
                          <span className="mp-referral-card__billing-value">
                            {formatCurrency(billing.balance_due)}
                          </span>
                        </div>
                        <div className="mp-referral-card__billing-item">
                          <span className="mp-referral-card__billing-label">Service expiry</span>
                          <span className="mp-referral-card__billing-value">
                            {formatDateOnly(billing.service_expiry)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </ProfileCard>
  );
}

function ProfileCard({ title, icon: Icon, compact = false, tone = "default", children }) {
  return (
    <div
      className={`pp-summary-card mp-card mp-card--${tone}${
        compact ? " mp-card--compact" : ""
      }`}
    >
      <div className="pp-summary-card__head pp-summary-card__head--inline">
        <span className="pp-summary-card__icon" aria-hidden>
          <Icon />
        </span>
        <h2 className="mp-card__title">{title}</h2>
      </div>
      <div className="pp-summary-card__body mp-card__body">{children}</div>
    </div>
  );
}

function extractReferralSummary(payload) {
  if (!payload || typeof payload !== "object") {
    return { referalCode: "", referredSchools: [] };
  }

  return {
    referalCode: String(payload.referal_code || payload.referral_code || "").trim(),
    referredSchools: Array.isArray(payload.referred_schools) ? payload.referred_schools : [],
  };
}

const MyProfile = () => {
  const loggedIn = localStorage.getItem("token") != null;
  const sessionStaff = SessionManager.getStaffDetails() || {};
  const [loading, setLoading] = useState(true);
  const [referralLoading, setReferralLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [referalCode, setReferalCode] = useState("");
  const [referredSchools, setReferredSchools] = useState([]);
  const [showChangePassword, setShowChangePassword] = useState(false);

  // Created By Cursor — profile first (lite), referrals deferred so page opens fast
  const fetchProfile = useCallback(async ({ soft = false } = {}) => {
    const staffData = SessionManager.getStaffDetails();
    const staffId = staffData?.staff_id;
    const schoolCode = SessionManager.getSchoolCode();

    if (!staffId) {
      myToast.notify_warning("Staff session is missing. Please login again.");
      setProfile(null);
      setReferalCode("");
      setReferredSchools([]);
      setLoading(false);
      setReferralLoading(false);
      return;
    }

    if (!soft) setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE()}getStaffProfile.php`, {
        params: { staff_id: staffId, lite: 1 },
      });

      if (
        data?.api_status === "OK" &&
        Array.isArray(data?.data) &&
        data.data.length > 0
      ) {
        setProfile(data.data[0]);
      } else {
        setProfile(null);
        myToast.notify_warning(data?.api_remark || "Could not load profile.");
      }
    } catch (error) {
      console.error("[MyProfile] profile error", error?.message || error);
      setProfile(null);
      myToast.notify_warning("Network error loading profile.");
    } finally {
      setLoading(false);
    }

    if (!schoolCode) {
      setReferalCode("");
      setReferredSchools([]);
      return;
    }

    setReferralLoading(true);
    try {
      const { data: referralData } = await axios.get(
        `${API_BASE()}getStaffReferralSummary.php`,
        { params: { staff_id: staffId, school_code: schoolCode } }
      );
      if (referralData?.api_status === "OK") {
        const summary = extractReferralSummary(referralData.data);
        setReferalCode(summary.referalCode);
        setReferredSchools(summary.referredSchools);
      } else {
        setReferalCode("");
        setReferredSchools([]);
      }
    } catch (error) {
      console.error("[MyProfile] referral error", error?.message || error);
      setReferalCode("");
      setReferredSchools([]);
    } finally {
      setReferralLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const qualifications = useMemo(() => {
    return Array.isArray(profile?.qualification) ? profile.qualification : [];
  }, [profile]);

  const displayProfile = profile || (sessionStaff.staff_id ? sessionStaff : null);

  const heroSubtitle = useMemo(() => {
    if (!displayProfile) return "View your staff profile details";
    return displayValue(displayProfile.staff_name);
  }, [displayProfile]);

  const heroChips = useMemo(() => {
    if (!displayProfile) return [];
    return [
      displayProfile.staff_role,
      displayProfile.department_name,
      displayProfile.staff_id ? `ID ${displayProfile.staff_id}` : null,
    ].filter(Boolean);
  }, [displayProfile]);

  if (!loggedIn) return <Redirect to="/Login" />;
  //Created By Cursor — only block first paint when no session stub
  if (loading && !displayProfile) return <MyLoader />;

  return (
    <div
      className="content payroll-page sm-page mp-page"
      style={{
        background: COLORS.white,
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div className="content-header" style={{ background: COLORS.primary_color_shade5 }}>
                <div className="leftside-content-header">
                  <ul className="breadcrumbs">
                    <li>
              <i className="fa fa-home" aria-hidden="true" style={{ color: COLORS.white }} />
              <span style={{ color: COLORS.white }}> My Profile</span>
                    </li>
                  </ul>
                </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            {displayProfile ? (
              displayProfile.staff_image_path ? (
                <img
                  src={displayProfile.staff_image_path}
                  alt=""
                  className="sm-hero__icon mp-hero__avatar"
                />
              ) : (
                <span
                  className="sm-hero__icon mp-hero__avatar mp-hero__avatar--fallback"
                  aria-hidden
                >
                  {getInitials(displayProfile.staff_name)}
                </span>
              )
            ) : (
              <div className="sm-hero__icon" aria-hidden>
                <FaUser />
              </div>
            )}
            <div>
              <h1 className="sm-hero__title">My Profile</h1>
              <p className="sm-hero__sub">{heroSubtitle}</p>
              {heroChips.length > 0 ? (
                <div className="mp-hero__chips">
                  {heroChips.map((chip) => (
                    <span key={chip} className="mp-hero__chip">
                      {chip}
                    </span>
                  ))}
                  </div>
              ) : null}
                      </div>
                    </div>
          <div className="sm-hero__tools">
            <button
              type="button"
              className={`pp-icon-btn ${loading || referralLoading ? "spinning" : ""}`}
              onClick={() => fetchProfile({ soft: true })}
              disabled={loading}
              aria-label="Refresh profile"
            >
              <FaSyncAlt aria-hidden />
            </button>
            <button
              type="button"
              className="btn lift payroll-cta d-inline-flex align-items-center gap-1"
              style={{ borderRadius: 999, fontWeight: 700, padding: "8px 16px", fontSize: 13 }}
              onClick={() => setShowChangePassword(true)}
            >
              <FaKey aria-hidden /> Change Password
            </button>
          </div>
        </header>

        <section className="sm-section" aria-label="Referrals management">
          {displayProfile ? (
            referralLoading && !referalCode && referredSchools.length === 0 ? (
              <div className="mp-referral-loading">Loading referrals…</div>
            ) : (
              <ReferralsSection referalCode={referalCode} referredSchools={referredSchools} />
            )
          ) : null}
        </section>

        <section className="sm-section sm-section--last" aria-label="Profile details">
          {loading && !profile ? (
            <div className="mp-referral-loading">Loading profile details…</div>
          ) : !profile ? (
            <div className="sm-empty">
              <p className="sm-empty__title">Profile not available</p>
              <p className="sm-empty__hint">
                We could not load your staff profile. Try refreshing or login again.
              </p>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={() => fetchProfile()}
              >
                <FaSyncAlt aria-hidden /> Refresh
              </button>
            </div>
          ) : (
            <div className="mp-grid">
                <ProfileCard title="Profile Details" icon={FaUser} compact tone="profile">
                  <div className="mp-fields">
                    <CompactField label="Role" value={profile.staff_role} tone="work" />
                    <CompactField label="Department" value={profile.department_name} tone="work" />
                    <CompactField label="Joining" value={profile.joining_date} tone="work" />
                    <CompactField label="Registered" value={profile.registration_date} tone="work" />
                    <CompactField label="Phone" value={profile.staff_phone} tone="contact" />
                    <CompactField label="Email" value={profile.staff_email} tone="contact" />
                    <CompactField label="DOB" value={profile.staff_dob} tone="contact" />
                    <CompactField label="Gender" value={profile.staff_gender} tone="contact" />
                    <CompactField label="Nationality" value={profile.nationality} tone="personal" />
                    <CompactField label="Religion" value={profile.staff_religion} tone="personal" />
                    <CompactField label="Address" value={profile.staff_address} tone="personal" fullWidth />
                  </div>
                </ProfileCard>

                <ProfileCard title="Education" icon={FaGraduationCap} tone="education">
                  {qualifications.length === 0 ? (
                    <div className="mp-degree-empty">
                      <FaGraduationCap aria-hidden />
                      <span>No qualifications added yet</span>
                    </div>
                  ) : (
                    <div className="mp-degree-list">
                      {qualifications.map((item, index) => (
                        <div className="mp-degree-card" key={`${item.staff_degree || "qual"}-${index}`}>
                          <span className="mp-degree-card__icon" aria-hidden>
                            <FaGraduationCap />
                          </span>
                          <div className="mp-degree-card__body">
                            <div className="mp-degree-card__name">
                              {displayValue(item.staff_degree)}
                    </div>
                            <div className="mp-degree-card__meta">Qualification</div>
                  </div>
                          <span className="mp-degree-card__badge">
                            {displayValue(item.staff_percentage)}%
                          </span>
                </div>
              ))}
                    </div>
                  )}
                </ProfileCard>
            </div>
          )}
        </section>
      </div>
      {showChangePassword ? (
        <ChangePassword actionClose={() => setShowChangePassword(false)} />
      ) : null}
    </div>
  );
};

export default MyProfile;
